﻿function escapeExternalCheckoutHtml(value) {
  return String(value || '').replace(/[&<>"']/g, function(character) {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[character];
  });
}

function buildExternalCheckoutModal({ modalId, title, checkoutUrl, isPreOrder = false }) {
  const action = isPreOrder ? 'Pre-order' : 'Order';
  const actionLower = isPreOrder ? 'pre-order' : 'order';
  const logoUrl = window.theme?.externalCheckoutLogo || '';
  const escapedModalId = escapeExternalCheckoutHtml(modalId);
  const escapedTitle = escapeExternalCheckoutHtml(title);
  const escapedUrl = escapeExternalCheckoutHtml(checkoutUrl);

  return `
    <div id="${escapedModalId}" class="external-checkout-modal" data-external-checkout-modal data-external-checkout-url="${escapedUrl}" hidden>
      <div class="external-checkout-modal__overlay" data-external-checkout-close></div>
      <div class="external-checkout-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="${escapedModalId}-title">
        <button type="button" class="external-checkout-modal__close" data-external-checkout-close aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
        </button>
        ${logoUrl ? `<div class="external-checkout-modal__brand"><img src="${escapeExternalCheckoutHtml(logoUrl)}" alt="Dalstrong to StudioForge" loading="eager" fetchpriority="high" decoding="sync" width="352" height="75"></div>` : ''}
        <div class="external-checkout-modal__rule"></div>
        <h2 id="${escapedModalId}-title" class="external-checkout-modal__title">${action} ${escapedTitle}</h2>
        <p class="external-checkout-modal__copy">You're being redirected to <strong>studioforge.com</strong> to complete your ${actionLower}. You'll be leaving the Dalstrong site to finalize your order on the official StudioForge website.</p>
        <p class="external-checkout-modal__secure">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.25 3.75H3.75a1 1 0 0 0-1 1v7.5a1 1 0 0 0 1 1h7.5a1 1 0 0 0 1-1v-2.5M8 8l5.25-5.25M9.75 2.75h3.5v3.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Secure checkout on studioforge.com
        </p>
        <button type="button" class="external-checkout-modal__continue" data-external-checkout-continue>
          <span>Continue to StudioForge</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="external-checkout-modal__back" data-external-checkout-close>Go Back</button>
      </div>
    </div>
  `;
}

function deriveExternalCheckoutModalTitle(title, shortTitle = '') {
  if (shortTitle) return shortTitle;

  const titleParts = String(title || '').split(' | ').map(part => part.trim()).filter(Boolean);
  const preferredPart = titleParts.slice(1).find(part => {
    const lowerPart = part.toLowerCase();
    const isBrandOrSeries = ['studioforge', 'dalstrong', 'series', 'edition', 'collection'].some(keyword => lowerPart.includes(keyword));
    const looksLikePieceName = ['blade of', 'fury', 'edge', 'solitude', 'hellfire'].some(keyword => lowerPart.includes(keyword)) || part.includes('™');

    return !isBrandOrSeries && looksLikePieceName;
  });

  if (preferredPart) return preferredPart;
  return titleParts[1] || titleParts[0] || title;
}

class ProductCard {
  constructor(node, ratingData, netflixBadgeUrl = '', collectionInternationalDiscount = false, promoBadge = null, cardIndex = 1) {
    this.product = node;
    this.ratingAvg = ratingData.avg;
    this.netflixBadgeUrl = netflixBadgeUrl;
    this.cardIndex = cardIndex;
    this.collection2x = ['go-cook-sweepstakes-2025-2x-entries'];
    this.collection3x = ['go-cook-sweepstakes-2025-3x-entries'];
    this.collection4x = ['go-cook-sweepstakes-2025-4x-entries'];
    this.isSweepstakes = false;
    this.collectionInternationalDiscount = collectionInternationalDiscount;
    this.promoBadge = promoBadge;
  }

  renderStars() {
    if (this.ratingAvg <= 3.0) return '';

    const productId = (this.product.id || '').split('/').pop();
    return `
      <div class="yotpo-category-stars">
        <div class="yotpo-widget-instance" data-yotpo-instance-id="1287695" data-yotpo-product-id="${productId}" data-yotpo-cart-product-id="" data-yotpo-section-id="collection"></div>
      </div>
    `;
  }
  
  getSaleInfo(product) {
    // Use cents for precise integer arithmetic (matching Liquid)
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) {
      return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
    }

    let metafieldDiscount = 0;
    let isFixedAmount = false;

    // Priority 1: Fixed Amount Off
    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      metafieldDiscount = Math.round(rawAmount * 100);
      isFixedAmount = true;
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 2: Product Percentage Off
    const rawOff = parseFloat(product.percentOff?.value) || 0;
    if (rawOff > 0) {
      // Use integer arithmetic in cents: (priceInCents * rawOff) / 100
      metafieldDiscount = Math.round((priceInCents * rawOff) / 100);
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 3: Collection Percentage Off
    const collections = product.collections?.edges || [];
    for (const { node: collection } of collections) {
      const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
      if (collectionOff > 0) {
        // Use integer arithmetic in cents: (priceInCents * collectionOff) / 100
        metafieldDiscount = Math.round((priceInCents * collectionOff) / 100);
        return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
      }
    }

    return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
  }

  getPointsInfo(price, scriptSavedAmount, productCollections) {
    const discountedPrice = scriptSavedAmount ? price - scriptSavedAmount : price;
    let points = Math.round(discountedPrice);

    const handles = productCollections.map(edge => edge.node.handle);
    let multiplier = 1;

    const body = document.body;

    if (body.classList.contains('body--sweepstakes__bonus')) {
      multiplier = 2;
      this.isSweepstakes = true;
    } else if (body.classList.contains('body--sweepstakes__bonus_3x')) {
      multiplier = 3;
      this.isSweepstakes = true;
    }

    if (handles.some(handle => this.collection4x.includes(handle)) && multiplier < 4) {
      multiplier = 4;
    } else if (handles.some(handle => this.collection3x.includes(handle)) && multiplier < 3) {
      multiplier = 3;
    } else if (handles.some(handle => this.collection2x.includes(handle)) && multiplier < 2) {
      multiplier = 2;
    }

    points = Math.floor(points * multiplier);
    return { points, multiplier, handles };
  }

  getCountdownTimer() {
    // Get current time in EST
    const now = new Date();
    const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    
    // Set end of day in EST (11:59:59 PM)
    const endOfDay = new Date(estTime);
    endOfDay.setHours(23, 59, 59, 999);
    
    // Calculate difference in milliseconds
    const diff = endOfDay - estTime;
    
    if (diff <= 0) {
      return '00:00:00';
    }
    
    // Calculate hours, minutes, seconds
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    // Format with leading zeros
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }


  startCountdown(timerElement) {
    const updateTimer = () => {
      timerElement.textContent = this.getCountdownTimer();
    };
    
    // Update every second
    setInterval(updateTimer, 1000);
  }

  render() {
    const title = this.product.title || '';
    const parts = title.split(' | ');

    const productName = parts[0];
    const series = parts.find(part =>
      /series|edition/i.test(part)
    ) || parts.find(part => part.includes('Dalstrong')) || '';
    const collections = this.product.collections?.edges?.map(edge => edge.node) || [];
    const normalizedSeries = series.trim()
    let matchingCollection = collections.find(
      col => col.title?.trim() === normalizedSeries
    );

    if (!matchingCollection) {
      matchingCollection = collections.find(
        col => normalizedSeries.toLowerCase().includes(col.title?.toLowerCase())
      );
    }
    
    const collectionSpecs = this.product.featuresBenefits?.value || matchingCollection?.metafield?.value || '';
    const collectionSpecsShort = this.product.materials?.value || matchingCollection?.mobileSpecs?.value || collectionSpecs || '';
    const variantId = this.product.variants.edges[0].node.id.split('/').pop();
    const externalCheckoutUrl = this.product.externalCheckoutUrl?.value || '';
    const externalCheckoutModalId = `ExternalCheckoutModal-grid-js-${variantId}`;
    const externalCheckoutModalTitle = deriveExternalCheckoutModalTitle(title);
    
    // Get image from metafields or fallback to featured image
    let imageUrl = '';
    const cardImageUrl = this.product.productCardImage?.reference?.image?.url;
    if (cardImageUrl) {
      imageUrl = cardImageUrl;
    } else {
      imageUrl = this.product.metafield?.reference?.image?.url || this.product.featuredImage?.url || this.product.images?.edges[0]?.node.url || '';
    }

    // Work in cents for precise integer arithmetic (matching Liquid)
    const priceInCents = Math.round(parseFloat(this.product.priceRange.minVariantPrice.amount) * 100);
    const compareAtPriceInCents = Math.round(parseFloat(this.product.compareAtPriceRange.maxVariantPrice.amount) * 100);
    const currency = this.product.priceRange.minVariantPrice.currencyCode;
    const isSoldOut = !this.product.availableForSale;
    const isComingSoon = this.product.tags.includes('coming_soon');
    const isStudioForge = this.product.tags.includes('StudioForge');
    const isPreOrder = this.product.tags.includes('pre-order');
    const isNew = this.product.tags.includes('new');
    const hasBundleSave = this.product.tags.includes('pcs_block') || this.product.tags.includes('individual_pcs');
    const isBestSeller = this.product.tags.includes('BEST_SELLER');
    const hasBonusSheath = this.product.tags.includes('abs_sheath') || this.product.tags.includes('nylon_sheath') || this.product.tags.includes('leather_sheath') || this.product.tags.includes('wood_sheath') || this.product.tags.includes('bonus_sheath');
    const isNetflixBadge = this.product.tags.includes('Netflix_badge');
    const isBfcmDeal = this.product.tags.includes('bfcm-daily-deal');
    const isDDayDeal = this.product.tags.includes('d-day-daily-deal');
    const isBannerProd = this.product.tags.includes('banner-prod');

    const promoBadge = this.promoBadge || {};
    const promoBadgeText = (promoBadge.text || '').toString().trim();
    let promoScheduleActive = true;
    if (promoBadge.start_at) {
      const startTs = Date.parse(promoBadge.start_at);
      if (!isNaN(startTs) && Date.now() < startTs) promoScheduleActive = false;
    }
    if (promoScheduleActive && promoBadge.end_at) {
      const endTs = Date.parse(promoBadge.end_at);
      if (!isNaN(endTs) && Date.now() >= endTs) promoScheduleActive = false;
    }
    // Fall promo: fall-50-off / fall-free-bonus tags get a badge with the same priority as the promo badge.
    const fallBadgeText = this.product.tags.includes('fall-50-off') ? '50% ADD-ON'
      : this.product.tags.includes('fall-free-bonus') ? 'FREE BONUS' : '';
    const hasFallAddon = !isSoldOut && !isPreOrder && fallBadgeText !== '';
    const hasPromoBadge = hasFallAddon || (!isSoldOut && !isPreOrder && promoBadgeText !== '' && this.product.tags.includes('promotion') && promoScheduleActive);
    let promoBadgeHtml = '';
    if (hasFallAddon) {
      promoBadgeHtml = `<div class="badge--new grid-product__tag--promo grid-product__tag--fall-addon">${fallBadgeText}</div>`;
    } else if (hasPromoBadge) {
      const promoStyles = [];
      if (promoBadge.text_color) promoStyles.push(`color: ${escapeExternalCheckoutHtml(promoBadge.text_color)}`);
      if (promoBadge.background_color) promoStyles.push(`background-color: ${escapeExternalCheckoutHtml(promoBadge.background_color)}`);
      const promoStyleAttr = promoStyles.length ? ` style="${promoStyles.join('; ')}"` : '';
      promoBadgeHtml = `<div class="badge--new grid-product__tag--promo"${promoStyleAttr}>${escapeExternalCheckoutHtml(promoBadgeText)}</div>`;
    }

    // Get metafield discount info (returns discount in cents)
    const { metafieldDiscount, isFixedAmount, hasMetafieldDiscount } = this.getSaleInfo(this.product);
    const isLimitedTimeDeal = hasMetafieldDiscount;
    const hasCompareAtPrice = compareAtPriceInCents > priceInCents;

    // Strike out the regular price when a metafield discount applies; compare-at otherwise
    const anchorPriceInCents = (hasCompareAtPrice && !hasMetafieldDiscount) ? compareAtPriceInCents : priceInCents;
    const finalPriceInCents = hasMetafieldDiscount ? (priceInCents - metafieldDiscount) : priceInCents;

    // Calculate total savings - using integer arithmetic
    const totalSavingsInCents = anchorPriceInCents - finalPriceInCents;
    const hasDiscount = totalSavingsInCents > 0;

    // Convert back to dollars for display
    const price = priceInCents / 100;
    const compareAtPrice = compareAtPriceInCents / 100;
    const finalPrice = finalPriceInCents / 100;
    const anchorPrice = anchorPriceInCents / 100;

    // Format discount label (dollar amount for fixed discounts, percentage for others)
    // Label reflects only the metafield discount, relative to price (not compare-at)
    let discountLabel = '';
    if (hasMetafieldDiscount && hasDiscount) {
      if (isFixedAmount) {
        discountLabel = `$${Math.floor(metafieldDiscount / 100)} OFF`;
      } else {
        discountLabel = `-${Math.round((metafieldDiscount * 100) / priceInCents)}%`;
      }
    }
    const { points, multiplier } = this.getPointsInfo(price, metafieldDiscount, this.product.collections.edges);

    let pointsBadgeHtml = '';
    if (multiplier > 1 && this.product.availableForSale) {
      const productTags = this.product.tags;
      const bestSeller = productTags.includes('BEST_SELLER');
      const isBatmanKnife = productTags.includes('Batman series') && productTags.includes('knife');
      const isNetflix = productTags.includes('Netflix_badge');
      const hasSheath = productTags.some(tag =>
        ['abs_sheath','nylon_sheath','wood_sheath','leather_sheath'].includes(tag)
      );

      let extraClasses = '';
      if (isBatmanKnife) extraClasses += ' badge--points__batman';
      if (isNetflix)    extraClasses += ' badge--points__netflix';
      if (!hasMetafieldDiscount && !bestSeller && !isNew && !hasSheath) {
        extraClasses += ' badge--points__single';
      }

      pointsBadgeHtml = `
        <span class="badge--new grid-product__tag--points${extraClasses}">
          ${points} entries
        </span>
      `;
    }

    const hasSweepstakesCollection = collections.find(
      c => c.title && c.title.toLowerCase().includes('sweepstakes')
    );

    const productCard = document.createElement('div');
    productCard.className = 'grid__item';
    productCard.classList.add('new-card');
    productCard.innerHTML = `
      <div class="grid-product__content ${isBfcmDeal ? ' bfcm-daily' : ''}${isDDayDeal ? ' d-day-daily' : ''}${isBannerProd ? ' banner-prod' : ''}">
        <div class="badge-overlay${this.isSweepstakes ? ' hide' : ''}">
          ${isPreOrder && !isSoldOut ? '<div class="badge--new grid-product__tag--pre-order">PRE ORDER</div>' : ''}
          ${promoBadgeHtml}
          ${isLimitedTimeDeal && !hasPromoBadge && !isPreOrder && !isSoldOut ? '<div class="badge--new grid-product__tag--limited-time-deal">LIMITED TIME DEAL</div>' : ''}
          ${isNew && !hasPromoBadge && !isPreOrder && !isLimitedTimeDeal && !isSoldOut && !isNetflixBadge ? '<div class="badge--new grid-product__tag--new">NEW</div>' : ''}
          ${hasBundleSave && !hasPromoBadge && !isPreOrder && !isLimitedTimeDeal && !isNew && !isSoldOut && !isNetflixBadge ? '<div class="badge--new grid-product__tag--bundle-and-save">Bundle & Save</div>' : ''}
          ${isBestSeller && !hasPromoBadge && !isPreOrder && !isLimitedTimeDeal && !isNew && !hasBundleSave && !isSoldOut && !isNetflixBadge ? '<div class="badge--new grid-product__tag--best-seller">BESTSELLER</div>' : ''}
          ${isSoldOut ? (isComingSoon ? '<div class="badge--new grid-product__tag--coming-soon">COMING SOON</div>' : isStudioForge ? '' : '<div class="badge--new grid-product__tag--sold-out">SOLD OUT</div>') : ''}
        </div>
        ${isNetflixBadge && !isLimitedTimeDeal ? `<img src="${this.netflixBadgeUrl}" alt="Netflix badge" loading="lazy" class="badge--netflix">` : ''}
        ${isBfcmDeal && !isSoldOut ? `<div class="badge--daily-deal">🔥 Shop today's daily deaL</div>` : ''}
        ${isDDayDeal && !isSoldOut ? `<div class="badge--daily-deal">🔥 TODAY'S DEAL</div>` : ''}
        <div class="collection-product-wrapper">
          <div class="grid-product__image-mask">
            <a href="${externalCheckoutUrl ? escapeExternalCheckoutHtml(externalCheckoutUrl) : (isBannerProd ? 'https://dalstrong.com//collections/dalstrong-day-2026-daily-deals' : this.product.onlineStoreUrl)}"${externalCheckoutUrl ? ` data-external-checkout-trigger data-external-checkout-url="${escapeExternalCheckoutHtml(externalCheckoutUrl)}" aria-controls="${externalCheckoutModalId}"` : ''}>
              <img class="grid-product__image" src="${imageUrl}" alt="${escapeExternalCheckoutHtml((series || this.product.vendor || '') + ' _ ' + this.product.title + ' _ ' + this.cardIndex)}" loading="lazy">
            </a>
          </div>
          <div class="collection-product__info-wrapper">
            <div class="product-container__description">
              <a href="${externalCheckoutUrl ? escapeExternalCheckoutHtml(externalCheckoutUrl) : (isBannerProd ? 'https://dalstrong.com//collections/dalstrong-day-2026-daily-deals' : this.product.onlineStoreUrl)}" class="grid-product__link"${externalCheckoutUrl ? ` data-external-checkout-trigger data-external-checkout-url="${escapeExternalCheckoutHtml(externalCheckoutUrl)}" aria-controls="${externalCheckoutModalId}"` : ''}>
                <div class="collection-product__title">
                  <div class="collection-product__collection-title">
                    <div class="collection-product__series-title">${series || ''}</div>
                    ${this.renderStars()}
                  </div>
                  <div class="collection-product__product-title">${productName}</div>
                </div>
                ${isBannerProd
                  ? `<div class="banner-prod-desc">${this.product.descriptionHtml || ''}</div>`
                  : `<div class="collection-product__collection-specs">${collectionSpecs}</div>
                <div class="collection-product__collection-specs-short">${collectionSpecsShort}</div>`
                }
              </a>
            </div>
            <div class="collection-product__button-container">
              ${!isBannerProd ? `<div class="grid-collection-product__price${hasDiscount ? ' script-sale-price' : ''}">
                ${isComingSoon ? '' : `
                ${discountLabel ? `<span class="home-discount-percentage">${discountLabel}</span>` : ''}
                <span class="igPrice" data-variant-id="${variantId}">${finalPrice.toLocaleString('en-US', { style: 'currency', currency: currency })}</span>
                ${hasDiscount ? `<span class="home-card-discount" data-variant-id="${variantId}">${anchorPrice.toLocaleString('en-US', { style: 'currency', currency: currency }).slice(0, -3)}</span>` : ''}
                <div class="line-break"></div>
                ${hasBundleSave && !this.isSweepstakes && !hasMetafieldDiscount ? '<div class="badge--new badge--secondary grid-product__tag--best-value">BEST VALUE</div>' : ''}
                `}
                ${pointsBadgeHtml}
              </div>` : ''}
              <div class="collection-product__urgency-line${(hasMetafieldDiscount || isDDayDeal) ? ' timer-line' : ''}${hasBundleSave ? ' hidden-line' : ''}">
                ${(!isComingSoon && (hasMetafieldDiscount || isDDayDeal)) ? ((isBfcmDeal || isDDayDeal) ? `<span class="countdown-timer" data-variant-id="${variantId}">${this.getCountdownTimer()}</span> Remaining to End.` : `Limited Time Deal!`) : ''}
              </div>
            </div>
          </div>
          ${externalCheckoutUrl ? `
            <button
              type="button"
              class="add-to-cart btn--full"
              data-external-checkout-trigger
              data-external-checkout-url="${escapeExternalCheckoutHtml(externalCheckoutUrl)}"
              aria-label="Shop ${escapeExternalCheckoutHtml(this.product.title)} on Studioforge"
              aria-controls="${externalCheckoutModalId}">
              Shop on Studioforge
            </button>
            ${buildExternalCheckoutModal({
              modalId: externalCheckoutModalId,
              title: externalCheckoutModalTitle,
              checkoutUrl: externalCheckoutUrl,
              isPreOrder
            })}
          `
          : isPreOrder ? `
            <button
              type="button"
              class="add-to-cart btn--full"
              data-external-checkout-trigger
              data-external-checkout-url="${escapeExternalCheckoutHtml(this.product.onlineStoreUrl)}"
              aria-label="Pre-order ${escapeExternalCheckoutHtml(this.product.title)}"
              aria-controls="${externalCheckoutModalId}">
              Pre-order
            </button>
            ${buildExternalCheckoutModal({
              modalId: externalCheckoutModalId,
              title: externalCheckoutModalTitle,
              checkoutUrl: this.product.onlineStoreUrl,
              isPreOrder
            })}
          `
          : isBannerProd ? `
            <a
              href="https://dalstrong.com//collections/dalstrong-day-2026-daily-deals"
              class="add-to-cart btn--full banner-prod-btn"
              aria-label="Shop the Deal"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
              SHOP THE DEAL
            </a>
          `
          : isSoldOut ? `
            <button
              type="button"
              class="add-to-cart btn--full notify-me-btn"
              data-notify-variant-id="${variantId}"
              data-notify-product-title="${escapeExternalCheckoutHtml(this.product.title)}"
              aria-label="Notify me when ${escapeExternalCheckoutHtml(this.product.title)} is ${isComingSoon ? 'available' : 'back in stock'}"
            >
              Notify Me
            </button>
          `
          :
          `
            <button
              class="add-to-cart btn--full"
              data-native-pre-order-btn
              data-starting-variant="${variantId}"
              data-variant-id="${variantId}"
              aria-label="Add ${escapeExternalCheckoutHtml(this.product.title)} to cart"
            >
              Add to Cart
            </button>
          `}
        </div>
      </div>
    `;

    const imageWrapper = productCard.querySelector('.grid-product__image');
    const handles = this.product.collections?.edges?.map(edge => edge.node.handle) || [];
    let extraImageHtml = '';
    if (this.product.availableForSale) {
      const body = document.body;

      let badgeType = '';

      if (body.classList.contains('body--sweepstakes__bonus')) {
        badgeType = '2x';
      } else if (body.classList.contains('body--sweepstakes__bonus_3x')) {
        badgeType = '3x';
      }

      if (handles.some(handle => this.collection4x.includes(handle))) {
        badgeType = '4x';
      } else if (handles.some(handle => this.collection3x.includes(handle)) && badgeType !== '4x') {
        badgeType = '3x';
      } else if (handles.some(handle => this.collection2x.includes(handle)) && !['3x','4x'].includes(badgeType)) {
        badgeType = '2x';
      }

      if (badgeType) {
        const badgeMap = {
          '2x': 'https://cdn.shopify.com/s/files/1/1728/9189/files/Frame_5292.png?v=1759255810',
          '3x': 'https://cdn.shopify.com/s/files/1/1728/9189/files/3X_Entries.png?v=1759262822',
          '4x': 'https://cdn.shopify.com/s/files/1/1728/9189/files/4X_Entries.png?v=1759262822'
        };
        extraImageHtml = `<img class="extra-badge" src="${badgeMap[badgeType]}" alt="${badgeType} Entries" loading="lazy">`;
      }
    }

    if (imageWrapper && extraImageHtml) {
      imageWrapper.insertAdjacentHTML('afterend', extraImageHtml);
    }
    
    const addToCartButton = productCard.querySelector('button.add-to-cart:not([data-external-checkout-trigger])');
    if (addToCartButton && !isSoldOut) {
      addToCartButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        })
        .then(response => response.json())
        .then(data => {
          document.dispatchEvent(new CustomEvent('cart:build'));
        })
        .then(() => {
          document.querySelector('.js-drawer-open-cart').click();
        })
        .catch(error => {
          console.error('Error adding item to cart:', error);
        });
      });
    }

    if (isDDayDeal || (hasMetafieldDiscount && isBfcmDeal)) {
      const timerElement = productCard.querySelector('.countdown-timer');
      if (timerElement) {
        this.startCountdown(timerElement);
      }
    }


    return productCard;
  }
}

class CollectionGridNew extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.isLoading = false;
    this.defaultCollectionHandle = this.dataset.defaultCollectionHandle;
    this.slides = this.querySelectorAll('.collection-grid-new__slide');
    this.defaultFilterTypes = this.dataset.filter;
    this.currentFilterTypes = {};
    this.selectedOptions = [];
    this.openFilterSections = new Set();
    this.customCardsIndexes = [0, 7, 9, 14, 16, 21, 27, 29, 32];
    this.customCards = [];
    this.customCardsData = [];
    this.activeCollectionHandle = this.defaultCollectionHandle;
    this.dalstrongLoaderElement = `
      <div class="collection-grid-new-loading-bg">
        <div class="animating-logo-wrapper" data-image-src="${ this.dataset.loaderImageUrl }"></div>
      </div>
    `;
    this.pageSize = parseInt(this.dataset.initialCount) || 48;
    this.filteredProducts = [];
    this.currentPage = 1;
    this.initialRenderDone = true;
    this.totalProductsCount = parseInt(this.dataset.totalCount) || 0;
    this.reviewsRatings = this.parseReviewsRatings();
    this.netflixBadgeUrl = this.dataset.netflixBadge;
    try {
      this.promoBadge = JSON.parse(this.dataset.promoBadge || 'null');
    } catch (e) {
      this.promoBadge = null;
    }
    this.collection2x = ['go-cook-sweepstakes-2025-2x-entries'];
    this.collection3x = ['go-cook-sweepstakes-2025-3x-entries'];
    this.collection4x = ['go-cook-sweepstakes-2025-4x-entries'];
    this.collectionInternationalDiscount = false;
    this.productsFetched = false;
    this.isFetching = false;
    this.currentSortKey = 'COLLECTION_DEFAULT';
    this.currentReverse = false;
    this.abortController = null;
    this.allMinPrice = 0;
    this.allMaxPrice = 0;
    this.currentMinPrice = null;
    this.currentMaxPrice = null;
    this.hasUrlFilters = false;
  }

  get optionPicker() { return this.querySelector('.option-picker'); }
  get optionPickerBtn() { return this.querySelector('.option-picker-btn'); }
  get filterPicker() { return this.querySelector('.filter-picker'); }
  get optionPickerList() { return this.querySelector('.option-picker-list'); }
  get sortPickerList() { return this.querySelector('.sort-picker-list'); }

  connectedCallback() {
    try { this.initSwiper(); } catch(e) { console.warn('Swiper init failed:', e); }
    this.setupEventListeners();

    const collectionCounterElement = this.querySelector('.collection-counter');
    if (collectionCounterElement && this.totalProductsCount > 0) {
      collectionCounterElement.textContent = `(${this.totalProductsCount} results)`;
    }

    this.applyUrlFilters();
  }

  applyUrlFilters() {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params.get('filter');
    if (!filterParam) return;

    this.selectedOptions = filterParam.split(',').map(v => {
      const trimmed = v.trim();
      // Bare numbers are treated as discount percentages (e.g. "50" → "50%")
      return /^\d+$/.test(trimmed) ? `${trimmed}%` : trimmed;
    }).filter(Boolean);
    if (this.selectedOptions.length > 0) {
      this.hasUrlFilters = true;
      // Hide server-rendered products, pagination, and counter while fetching
      const productGrid = this.querySelector('.product-grid');
      if (productGrid) productGrid.innerHTML = '';
      const pagination = this.querySelector('.collection-pagination');
      if (pagination) pagination.style.display = 'none';
      const counter = this.querySelector('.collection-counter');
      if (counter) counter.textContent = '';

      this.fetchCollectionProducts(true);
    }
  }

  setupEventListeners() {
    const subcollectionsEl = this.querySelector('.collection-grid-new__subcollections');
    if (subcollectionsEl) {
      subcollectionsEl.addEventListener('click', (e) => {
        const slide = e.target.closest('.collection-grid-new__slide');
        if (!slide || slide.classList.contains('active')) return;

        this.querySelectorAll('.collection-grid-new__slide').forEach(s => s.classList.remove('active'));
        slide.classList.add('active');
        if (this.optionPickerList) {
          this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
        }
        this.selectedOptions = [];
        this.currentMinPrice = null;
        this.currentMaxPrice = null;
        this.fetchCollectionProducts();
      });
    }
    this.addEventListener('click', (event) => {
      const { optionPickerBtn, filterPicker } = this;
      
      if (!optionPickerBtn || !filterPicker) return;

      const isClickOnButton = optionPickerBtn.contains(event.target);
      const isClickOnClose = event.target.closest('.option-picker-close') || event.target.closest('.sort-picker-close') || event.target.closest('.filter-picker-overlay');
      const isClickInsideList = event.target.closest('.option-picker-list-container');

      if (isClickOnButton) {
        if (!filterPicker.classList.contains('active')) {
          if (!this.productsFetched && !this.isFetching) {
            this.fetchCollectionProducts(false);
          }
          filterPicker.classList.add('active');
          this.optionPicker?.classList.add('is-open');
          document.body.classList.add('body-no-scroll');
          document.documentElement.classList.add('body-no-scroll');
        } else {
          filterPicker.classList.remove('active');
          this.optionPicker?.classList.remove('is-open');
          document.body.classList.remove('body-no-scroll');
          document.documentElement.classList.remove('body-no-scroll');
        }
        return;
      }
      
      if (isClickOnClose || (!isClickInsideList && filterPicker.classList.contains('active'))) {
        this.closeDrawer();
      }
    });

    this.optionPickerList?.addEventListener('click', (event) => {
      const title = event.target.closest('.option-picker-list-item-title');
      if (title) {
        const item = title.closest('.option-picker-list-item');
        const sectionTitle = title.querySelector('span')?.textContent?.trim();

        item.classList.toggle('active');

        if (sectionTitle) {
          if (item.classList.contains('active')) this.openFilterSections.add(sectionTitle);
          else this.openFilterSections.delete(sectionTitle);
        }

        this.scrollAccordionIntoView(item);
        return;
      }

      const item = event.target.closest('.sub-item');
      if (item) {
        event.preventDefault();
        event.stopPropagation();
        const checkbox = item.querySelector('.custom-checkbox');
        checkbox?.classList.toggle('checked');
        this.updateSelectedOptions(item);
      }
    });

    this.optionPickerList?.addEventListener('input', (event) => {
      const range = event.target.closest('input[type="range"]');
      if (!range) return;

      const container = range.closest('.price-range-container');
      if (!container) return;

      const minNum   = container.querySelector('.input-min');
      const maxNum   = container.querySelector('.input-max');
      const minRange = container.querySelector('.range-min');
      const maxRange = container.querySelector('.range-max');
      const displayMin = container.querySelector('.display-min');
      const displayMax = container.querySelector('.display-max');

      if (range.classList.contains('range-min')) {
        if (parseInt(minRange.value) > parseInt(maxRange.value)) minRange.value = maxRange.value;
        minNum.value = minRange.value;
      } else {
        if (parseInt(maxRange.value) < parseInt(minRange.value)) maxRange.value = minRange.value;
        maxNum.value = maxRange.value;
      }

      displayMin.textContent = `$${Math.floor(minRange.value)}`;
      displayMax.textContent = `$${Math.ceil(maxRange.value)}`;
      this.updatePriceSliderUI(container);

      if (this.priceTimeout) clearTimeout(this.priceTimeout);
      this.priceTimeout = setTimeout(() => {
        this.currentMinPrice = parseFloat(minRange.value);
        this.currentMaxPrice = parseFloat(maxRange.value);

        if (isNaN(this.currentMinPrice)) this.currentMinPrice = this.allMinPrice;
        if (isNaN(this.currentMaxPrice)) this.currentMaxPrice = this.allMaxPrice;

        if (this.productsFetched) this.filterAndRenderProducts();
      }, 250);
    });

    this.optionPickerList?.addEventListener('blur', (event) => {
      const num = event.target.closest('input.price-input');
      if (!num) return;

      const container = num.closest('.price-range-container');
      if (!container) return;

      const minNum   = container.querySelector('.input-min');
      const maxNum   = container.querySelector('.input-max');
      const minRange = container.querySelector('.range-min');
      const maxRange = container.querySelector('.range-max');
      const displayMin = container.querySelector('.display-min');
      const displayMax = container.querySelector('.display-max');

      let minVal = parseFloat(minNum.value);
      let maxVal = parseFloat(maxNum.value);

      if (isNaN(minVal)) minVal = parseFloat(minRange.value);
      if (isNaN(maxVal)) maxVal = parseFloat(maxRange.value);

      minVal = Math.max(this.allMinPrice, Math.min(minVal, this.allMaxPrice));
      maxVal = Math.max(this.allMinPrice, Math.min(maxVal, this.allMaxPrice));

      if (minVal > maxVal) {
        if (num.classList.contains('input-min')) maxVal = minVal;
        else minVal = maxVal;
      }

      minRange.value = String(minVal);
      maxRange.value = String(maxVal);
      minNum.value = String(minVal);
      maxNum.value = String(maxVal);

      displayMin.textContent = `$${Math.floor(minVal)}`;
      displayMax.textContent = `$${Math.ceil(maxVal)}`;
      this.updatePriceSliderUI(container);

      this.currentMinPrice = minVal;
      this.currentMaxPrice = maxVal;

      if (this.productsFetched) this.filterAndRenderProducts();
    }, true);

    this.optionPickerList?.addEventListener('keydown', (event) => {
      const num = event.target.closest('input.price-input');
      if (!num) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        num.blur();
      }
    });

    this.sortPickerList?.addEventListener('click', (event) => {
      const item = event.target.closest('.sort-picker-item');
      if (item) {
        this.updateSort(item);
        this.optionPicker?.classList.remove('is-open');
      }
    });

    this.addEventListener('click', async (e) => {
      const loadMoreBtn = e.target.closest('.collection-pagination-load-more');
      if (!loadMoreBtn || this.isFetching) return;

      if (!this.productsFetched) {
        const originalText = loadMoreBtn.textContent;
        loadMoreBtn.textContent = 'LOADING...';
        await this.fetchCollectionProducts();
        loadMoreBtn.textContent = originalText;
      }

      this.currentPage += 1;
      const start = (this.currentPage - 1) * this.pageSize;
      const totalCount = this.filteredProducts.length;
      const end = Math.min(start + this.pageSize, totalCount);
      const nextProducts = this.filteredProducts.slice(start, end);
      
      this.renderProducts(nextProducts, true); 
      this.renderPaginationControls(totalCount);
    });
  }

  scrollAccordionIntoView(sectionTitleOrItem) {
    const scroller = this.optionPickerList;
    if (!scroller) return;

    const resolveItem = () => {
      if (sectionTitleOrItem instanceof Element) return sectionTitleOrItem;

      const title = (sectionTitleOrItem || '').trim();
      if (!title) return null;

      return [...scroller.querySelectorAll('.option-picker-list-item')].find(li => {
        const t = li.querySelector('.option-picker-list-item-title span')?.textContent?.trim();
        return t === title;
      }) || null;
    };

    const doScroll = () => {
      const item = resolveItem();
      if (!item) return;

      const padding = 8;

      const stickyHeader = this.querySelector('.option-picker-list-header');
      const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;

      scroller.scrollTo({
        top: item.offsetTop - headerOffset - padding,
        behavior: 'smooth'
      });
    };

    requestAnimationFrame(() => requestAnimationFrame(doScroll));

    clearTimeout(this._accordionScrollT);
    this._accordionScrollT = setTimeout(() => {
      requestAnimationFrame(doScroll);
    }, 320);
  }

  closeDrawer() {
    this.filterPicker?.classList.remove('active');
    this.optionPicker?.classList.remove('is-open');
    document.body.classList.remove('body-no-scroll');
    document.documentElement.classList.remove('body-no-scroll');
  }

  getPointsInfo(price, scriptSavedAmount, productCollections) {
    const discountedPrice = scriptSavedAmount ? price - scriptSavedAmount : price;
    let points = Math.round(discountedPrice);

    const handles = productCollections.map(edge => edge.node.handle);
    let multiplier = 1;

    const body = document.body;

    if (body.classList.contains('body--sweepstakes__bonus')) {
      multiplier = 2;
    } else if (body.classList.contains('body--sweepstakes__bonus_3x')) {
      multiplier = 3;
    }

    if (handles.some(handle => this.collection4x.includes(handle)) && multiplier < 4) {
      multiplier = 4;
    } else if (handles.some(handle => this.collection3x.includes(handle)) && multiplier < 3) {
      multiplier = 3;
    } else if (handles.some(handle => this.collection2x.includes(handle)) && multiplier < 2) {
      multiplier = 2;
    }

    points = Math.floor(points * multiplier);
    return { points, multiplier, handles };
  }

  parseReviewsRatings() {
    const ratingsJson = document.getElementById('reviews-ratings-json');
    if (ratingsJson) {
      try {
        return JSON.parse(ratingsJson.textContent);
      } catch (e) {
        console.error('Failed to parse reviews ratings JSON:', e);
      }
    }
    return {};
  }

  renderProducts(products, append = false) {
    let productGrid = this.querySelector('.product-grid');
    if (!productGrid) {
      productGrid = document.createElement('div');
      productGrid.className = 'product-grid';
      const optionPicker = this.querySelector('.option-picker');
      if (optionPicker) {
        optionPicker.after(productGrid);
      } else {
        this.appendChild(productGrid);
      }
    }
    
    if (!append) {
      productGrid.innerHTML = '';
    }

    const activeSlide = this.querySelector('.collection-grid-new__slide.active');
    const collectionTitle = activeSlide?.querySelector('.collection-grid-new__subtitle')?.textContent?.trim() || '';
    const collectionTitleElement = this.querySelector('.collection-title');
    const defaultCollectionTitle = collectionTitleElement?.textContent?.trim() || '';
    const collectionCounterElement = this.querySelector('.collection-counter');

    if(collectionTitleElement) {
      collectionTitleElement.textContent = defaultCollectionTitle;
    }
    
  if (collectionTitleElement && collectionTitle) {
      collectionTitleElement.textContent = collectionTitle;
    }

    productGrid.insertAdjacentHTML('beforeend', this.dalstrongLoaderElement);
    this.dalstrongLoader = productGrid.querySelector('.collection-grid-new-loading-bg');

    if (this.dalstrongLoader) {
      this.dalstrongLoader.classList.add('active');
      const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
      if (logoWrapper) {
        logoWrapper.insertAdjacentHTML('beforeend', `
          <img 
            src="${logoWrapper.dataset.imageSrc}"
            alt="Animated logo loader"
            width="100"
            height="88"
          />
        `);
      }
    }

    products.forEach(({ node }, index) => {
      if (this.customCardsIndexes.includes(index) && this.customCards.length > 0) {
        const customCard = this.customCards.shift();
        if (customCard) {
          productGrid.insertBefore(customCard, productGrid.children[index]);
        }
      }
      const netflixBadgeUrl = this.dataset.netflixBadge;
      const ratingData = this.reviewsRatings?.[node.handle] || { avg: 0 };

      const productCard = new ProductCard(node, ratingData, this.netflixBadgeUrl, this.collectionInternationalDiscount, this.promoBadge, index + 1);
      productGrid.appendChild(productCard.render());
    });
    
    if (collectionCounterElement) {
      collectionCounterElement.textContent = `(${this.filteredProducts.length} results)`;
    }
    setTimeout(() => {
      if (this.dalstrongLoader) {
        this.dalstrongLoader.classList.remove('active');
        const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
        if (logoWrapper) {
          logoWrapper.innerHTML = '';
        }
      }
    }, 500);

    this.reinitYotpoWidgets();
  }

  reinitYotpoWidgets() {
    try {
      if (window.yotpoWidgetsContainer?.initWidgets) {
        window.yotpoWidgetsContainer.initWidgets();
      } else if (window.yotpoWidgetsContainer?.guids) {
        Object.values(window.yotpoWidgetsContainer.guids).forEach(w => w?.initWidgets?.());
      }
    } catch (e) {
      console.warn('Yotpo reinit failed:', e);
    }
  }

  prepareCustomProductCardsData(cards) {
    this.customCardsData = cards || [];
    this.generateCustomCards(this.customCardsData);
  }

  generateCustomCards(cardData) {
    this.customCards = [];
    if (!cardData?.length) {
      return;
    }
    cardData.forEach((card, index) => {
      if (!card || !card.fields) {
        return;
      }
      const productField = card.fields.find(field => field.key === 'product');
      if (!productField?.reference) {
        return;
      }
      const mediaField = card.fields.find(field => field.key === 'media_file');
      let imageUrl = '';
      if (mediaField?.reference?.image?.url) {
        imageUrl = mediaField.reference.image.url;
      } else {
        imageUrl = productField.reference.featuredImage?.url || '';
      }
      const descriptionField = card.fields.find(field => field.key === 'description');
      const description = descriptionField?.value || '';
      const product = productField.reference;
      const title = product.title || '';
      const parts = title.split(' | ');
      const productName = parts[0];
      const series = parts.find(part =>
        /series|edition/i.test(part)
      ) || parts.find(part => part.includes('Dalstrong')) || '';
      const variantId = product.variants.edges[0].node.id.split('/').pop();
      const externalCheckoutUrl = product.externalCheckoutUrl?.value || '';
      const externalCheckoutModalId = `ExternalCheckoutModal-custom-card-js-${variantId}`;
      const externalCheckoutModalTitle = deriveExternalCheckoutModalTitle(title);
      const cardAlt = escapeExternalCheckoutHtml((series || product.vendor || '') + ' _ ' + product.title + ' _ ' + (index + 1));
      const customProductCardButton = externalCheckoutUrl
        ? `<button type="button" class="custom-product-card-button" data-external-checkout-trigger data-external-checkout-url="${escapeExternalCheckoutHtml(externalCheckoutUrl)}" aria-controls="${externalCheckoutModalId}">Shop on Studioforge</button>`
        : `<button class="custom-product-card-button" data-variant-id="${variantId}">ADD TO CART</button>`;
      const cardElement = document.createElement('div');
      const isWideCard = card.fields.find(field => field.key === 'width_2x')?.value === 'true';

      cardElement.className = index === 0 && isWideCard ? 'grid__item custom-product-card width-2x' : 'grid__item custom-product-card';
      let cardHTML = index === 0 && isWideCard ?
      `
        <div class="custom-product-card-image-container">
          <img class="custom-product-card-image" src="${imageUrl}" alt="${cardAlt}" loading="lazy">
        </div>
        <p class="custom-product-card-description">${description.split('\n').map(line => `<span>${line}</span><br>`).join('')}</p>
        <div class="custom-product-card-content desktop">
          <div class="custom-product-card-content-image-container">
            <img class="custom-product-card-content-image" src="${product.metafield?.reference?.image?.url || ''}" alt="${cardAlt}">
          </div>
          <div class="custom-product-card-content-text-container">
            <p class="custom-product-card-series-title">${series || ''}</p>
            <p class="custom-product-card-title">${productName}</p>
            ${customProductCardButton}
          </div>
        </div>
        <div class="custom-product-card-content mobile">
          <div class="custom-product-card-content-text-container">
            <div class="custom-product-card-titles-container">
              <p class="custom-product-card-series-title">${series || ''}</p>
              <p class="custom-product-card-title">${productName}</p>
            </div>
            ${customProductCardButton}
          </div>
        </div>
      `
      :
      `
        <div class="custom-product-card-image-container">
          <img class="custom-product-card-image" src="${imageUrl}" alt="${cardAlt}" loading="lazy">
        </div>
        <div class="custom-product-card-content">
          <p class="custom-product-card-series-title">${series || ''}</p>
          <p class="custom-product-card-title">${productName}</p>
        </div>
        <div class="custom-product-card-button-wrapper">
          ${customProductCardButton}
        </div>
      `;
      cardElement.innerHTML = cardHTML;
      if (externalCheckoutUrl) {
        cardElement.insertAdjacentHTML('beforeend', buildExternalCheckoutModal({
          modalId: externalCheckoutModalId,
          title: externalCheckoutModalTitle,
          checkoutUrl: externalCheckoutUrl,
          isPreOrder: product.tags?.includes('pre-order')
        }));
      }
      const addToCartButton = cardElement.querySelector('button.custom-product-card-button:not([data-external-checkout-trigger])');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1
              }]
            })
          })
          .then(response => response.json())
          .then(data => {
            document.dispatchEvent(new CustomEvent('cart:build'));
          })
          .then(() => {
            document.querySelector('.js-drawer-open-cart').click();
          })
          .catch(error => {
            console.error('Error adding item to cart:', error);
          });
        });
      }
      if (product.availableForSale) {
        this.customCards.push(cardElement);
      }
    });
  }

  getCountry() {
    const path = window.location.pathname.toLowerCase();
  
    if (path.startsWith('/en-gb')) return 'GB';
    if (path.startsWith('/en-aunz')) return 'AU';
    if (path.startsWith('/en-eu')) return 'DE';
  
    return 'US';
  }

  calculateDiscount(product) {
    if (!product.priceRange) return 0;
    
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) return 0;

    let metafieldDiscount = 0;

    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      metafieldDiscount = Math.round(rawAmount * 100);
    } else {
      const rawOff = parseFloat(product.percentOff?.value) || 0;
      if (rawOff > 0) {
        metafieldDiscount = Math.round((priceInCents * rawOff) / 100);
      } else {
        const collections = product.collections?.edges || [];
        for (const { node: collection } of collections) {
          const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
          if (collectionOff > 0) {
            metafieldDiscount = Math.round((priceInCents * collectionOff) / 100);
            break;
          }
        }
      }
    }

    const hasMetafieldDiscount = metafieldDiscount > 0;
    if (!hasMetafieldDiscount) return 0;

    // Discount % relative to price (not compare-at) so filter buckets match the card badge
    return Math.round((metafieldDiscount * 100) / priceInCents);
  }

  getFinalPrice(product) {
    if (!product?.priceRange?.minVariantPrice?.amount) return 0;

    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) return 0;

    let discountInCents = 0;
    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      discountInCents = Math.round(rawAmount * 100);
    } else {
      const rawOff = parseFloat(product.percentOff?.value) || 0;
      if (rawOff > 0) {
        discountInCents = Math.round((priceInCents * rawOff) / 100);
      } else {
        const collections = product.collections?.edges || [];
        for (const { node: collection } of collections) {
          const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
          if (collectionOff > 0) {
            discountInCents = Math.round((priceInCents * collectionOff) / 100);
            break;
          }
        }
      }
    }

    const finalInCents = priceInCents - discountInCents;
    return finalInCents / 100;
  }

  async fetchCollectionProducts(shouldRender = true) {
    if (this.isFetching && this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    console.warn('Fetching collection products...');
    this.isFetching = true;
    this.renderFilterTypes();

    try {
      const activeSlideEl = this.querySelector('.collection-grid-new__slide.active');
      if (activeSlideEl) {
        this.activeCollectionHandle = activeSlideEl.dataset.subcollectionHandle;
      }

      const query = `
        query getCollectionWithProducts(
          $handle: String!
          $cursor: String
          $country: CountryCode
          $sortKey: ProductCollectionSortKeys
          $reverse: Boolean
          ) @inContext(country: $country) {
          collection(handle: $handle) {
            id
            title
            description
            internationalDiscount: metafield(namespace: "custom", key: "international_discount") {
              value
              type
            }
            metafield(namespace: "custom", key: "filter_types") {
              value
              references(first: 50) {
                nodes {
                  ... on Metaobject {
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
            customProductCards: metafield(namespace: "custom", key: "custom_card_products") {
              value
              references(first: 50) {
                nodes {
                  ... on Metaobject {
                    id
                    type
                    fields {
                      key
                      value
                      reference {
                        ... on Product {
                          id
                          title
                          handle
                          onlineStoreUrl
                          availableForSale
                          featuredImage {
                            url
                            altText
                          }
                          metafield(namespace: "img", key: "card_image") {
                            value
                            reference {
                              ... on MediaImage {
                                image {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                          variants(first: 1) {
                            edges {
                              node {
                                id
                              }
                            }
                          }
                          amountOff: metafield(namespace: "custom", key: "amount_off") {
                            value
                          }
                          externalCheckoutUrl: metafield(namespace: "custom", key: "external_checkout_url") {
                            value
                          }
                          percentOff: metafield(namespace: "custom", key: "off") {
                            value
                          }
                          priceRange {
                            minVariantPrice {
                              amount
                            }
                          }
                          compareAtPriceRange {
                            maxVariantPrice {
                              amount
                            }
                          }
                          collections(first: 100) {
                            edges {
                              node {
                                title
                                handle
                                collectionOff: metafield(namespace: "custom", key: "off") {
                                  value
                                }
                              }
                            }
                          }
                          tags
                        }
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            products(first: 250, after: $cursor, sortKey: $sortKey, reverse: $reverse) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  title
                  handle
                  onlineStoreUrl
                  availableForSale
                  featuredImage {
                    url
                    altText
                  }
                  metafield(namespace: "img", key: "card_image") {
                    value
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                  productCardImage: metafield(namespace: "custom", key: "product_card_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                  featuresBenefits: metafield(namespace: "custom", key: "features_benefits") {
                    value
                  }
                  materials: metafield(namespace: "custom", key: "materials") {
                    value
                  }
                  amountOff: metafield(namespace: "custom", key: "amount_off") {
                    value
                  }
                  externalCheckoutUrl: metafield(namespace: "custom", key: "external_checkout_url") {
                    value
                  }
                  percentOff: metafield(namespace: "custom", key: "off") {
                    value
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        availableForSale
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                          currencyCode
                        }
                      }
                    }
                  }
                  tags
                  descriptionHtml
                  collections(first: 100) {
                    edges {
                      node {
                        title
                        handle
                        metafield(namespace: "custom", key: "cards_specifications") {
                          value
                        }
                        mobileSpecs: metafield(namespace: "custom", key: "cards_specifications_short") {
                          value
                        }
                        collectionOff: metafield(namespace: "custom", key: "off") {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      let allProductEdges = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const variables = {
          handle: this.activeCollectionHandle,
          cursor,
          country: this.getCountry(),
          sortKey: this.currentSortKey,
          reverse: this.currentReverse
        };

        const res = await fetch('https://dalstrong.com/api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': '1b6fb80e9d47a09bf6147e9744d97c2b'
          },
          body: JSON.stringify({ query, variables }),
          signal
        });

        const json = await res.json();
        if (json.errors) {
          console.error('GraphQL Errors:', json.errors);
          break;
        }

        const collection = json.data.collection;
        if (!collection) {
          console.warn('Collection not found:', this.activeCollectionHandle);
          hasNextPage = false;
          break;
        }
        const productData = collection.products;

        allProductEdges.push(...productData.edges);
        hasNextPage = productData.pageInfo.hasNextPage;
        cursor = productData.pageInfo.endCursor;

        if (collection.internationalDiscount?.value) {
          this.collectionInternationalDiscount = collection.internationalDiscount.value === 'true' || collection.internationalDiscount.value === true;
        } else {
          this.collectionInternationalDiscount = false;
        }

        if (collection.metafield?.references?.nodes) {
          this.configureFilterTypes(allProductEdges, collection.metafield);
          this.renderFilterTypes();
        } else if (this.defaultFilterTypes) {
          try {
            const filterTypesData = JSON.parse(this.defaultFilterTypes);
            const filterTypes = {
              references: {
                nodes: filterTypesData.map(type => ({
                  fields: [
                    { key: 'title', value: type.title },
                    { key: 'options', value: JSON.stringify(type.options) }
                  ]
                }))
              }
            };
            this.configureFilterTypes(allProductEdges, filterTypes);
            this.renderFilterTypes();
          } catch (e) {
            console.error('Error parsing filter types:', e);
            this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
          }
        } else {
          this.optionPickerList.querySelectorAll('.option-picker-list-item:not(.sort-by-sidebar-item)').forEach(el => el.remove());
        }

        if (collection.customProductCards?.references?.nodes) {
          this.prepareCustomProductCardsData(collection.customProductCards.references.nodes);
        }
      }

      if (signal.aborted) return;

      sessionStorage.setItem('allProducts', JSON.stringify(allProductEdges));
      this.allProducts = allProductEdges;
      this.productsFetched = true;

      if (this.hasUrlFilters && this.selectedOptions.length > 0) {
        const allOptions = Object.values(this.currentFilterTypes).flat();
        this.selectedOptions = this.selectedOptions.map(selected => {
          return allOptions.find(opt => opt.toLowerCase() === selected.toLowerCase()) || selected;
        });
      }

      if (shouldRender) {
        this.filterAndRenderProducts();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      console.error('Error fetching collection products:', error);
    } finally {
      if (!signal.aborted) {
        this.isFetching = false;
        this.renderFilterTypes();
      }
    }
  }

  configureFilterTypes(collection, metafield) {
    this.currentFilterTypes = {};
    this.filterOptionCounts = {};
    
    const dealOptions = ['On Sale', 'New Arrivals', 'In Stock', 'Best Sellers'];
    const availableDeals = new Set();
    
    collection.forEach(({ node: product }) => {
      const discount = this.calculateDiscount(product);
      const isNew = product.tags.some(tag => {
        const t = tag.toLowerCase();
        return t === 'new';
      });
      const isInStock = product.availableForSale;
      const isBestSeller = product.tags.some(tag => {
        const t = tag.toLowerCase();
        return t === 'best_seller' || t === 'best sellers';
      });

      if (discount > 0) {
        const opt = 'On Sale';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isNew) {
        const opt = 'New Arrivals';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isInStock) {
        const opt = 'In Stock';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
      if (isBestSeller) {
        const opt = 'Best Sellers';
        availableDeals.add(opt);
        this.filterOptionCounts[opt] = (this.filterOptionCounts[opt] || 0) + 1;
      }
    });

    if (availableDeals.size > 0) {
      this.currentFilterTypes['DEALS & STATUS'] = dealOptions.filter(opt => availableDeals.has(opt));
    }

    const filterTypeMetaobjects = metafield.references.nodes;

    this.discountTiers = [];
    filterTypeMetaobjects.forEach(metaobject => {
      const titleField = metaobject.fields.find(field => field.key === 'title');
      const optionsField = metaobject.fields.find(field => field.key === 'options');
      if (titleField && optionsField && titleField.value === 'Discount') {
        this.discountTiers = JSON.parse(optionsField.value)
          .map(o => parseInt(o))
          .filter(n => !isNaN(n) && n > 0)
          .sort((a, b) => b - a);
      }
    });

    collection.forEach(product => {
      filterTypeMetaobjects.forEach(metaobject => {
        const titleField = metaobject.fields.find(field => field.key === 'title');
        const optionsField = metaobject.fields.find(field => field.key === 'options');

        if (titleField && optionsField) {
          const title = titleField.value;
          const options = JSON.parse(optionsField.value);

          if (title === 'Discount') {
            const discount = this.calculateDiscount(product.node);
            if (discount > 0 && this.discountTiers.includes(discount)) {
              const bucket = `${discount}%`;
              if (!Array.isArray(this.currentFilterTypes[title])) {
                this.currentFilterTypes[title] = [];
              }
              if (!this.currentFilterTypes[title].includes(bucket)) {
                this.currentFilterTypes[title].push(bucket);
              }
              this.filterOptionCounts[bucket] = (this.filterOptionCounts[bucket] || 0) + 1;
            }
          } else {
            options.forEach(option => {
              if (product.node.tags.includes(option)) {
                if (!Array.isArray(this.currentFilterTypes[title])) {
                  this.currentFilterTypes[title] = [];
                }
                if (!this.currentFilterTypes[title].includes(option)) {
                  this.currentFilterTypes[title].push(option);
                }
                this.filterOptionCounts[option] = (this.filterOptionCounts[option] || 0) + 1;
              }
            });
          }
        }
      });
    });

    if (this.currentFilterTypes["Knife Length"]) {
      this.currentFilterTypes["Knife Length"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Cutting Board Size"]) {
      this.currentFilterTypes["Cutting Board Size"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Honing Rod Length"]) {
      this.currentFilterTypes["Honing Rod Length"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Grit"]) {
      this.currentFilterTypes["Grit"].sort((a, b) => {
        return parseFloat(a) - parseFloat(b);
      });
    }
    if (this.currentFilterTypes["Discount"]) {
      this.currentFilterTypes["Discount"].sort((a, b) => {
        return parseInt(a) - parseInt(b);
      });
    }

    // price range calculation
    let min = Infinity;
    let max = -Infinity;
    collection.forEach(({ node: product }) => {
      const price = parseFloat(product.priceRange.minVariantPrice.amount);
      if (price < min) min = price;
      if (price > max) max = price;
    });
    this.allMinPrice = min === Infinity ? 0 : Math.floor(min);
    this.allMaxPrice = max === -Infinity ? 0 : Math.ceil(max);

    if (this.currentMinPrice === null) this.currentMinPrice = this.allMinPrice;
    if (this.currentMaxPrice === null) this.currentMaxPrice = this.allMaxPrice;
  }

  renderFilterTypes() {
    if (!this.optionPickerList) return;

    if (this.isFetching && Object.keys(this.currentFilterTypes).length === 0) {
      if (!this.optionPickerList.querySelector('.loading-text')) {
        this.optionPickerList.insertAdjacentHTML(
          'beforeend',
          '<p class="loading-text" style="padding: 20px;">Loading...</p>'
        );
      }
      this.optionPickerBtn?.classList.remove('hide');
      return;
    }

    this.optionPickerList.querySelector('.loading-text')?.remove();

    const sortByItem = this.optionPickerList.querySelector('.sort-by-sidebar-item');

    this.optionPickerList.innerHTML = '';

    if (sortByItem) this.optionPickerList.appendChild(sortByItem);

    const priceItem = document.createElement('li');
    priceItem.classList.add('option-picker-list-item');
    if (this.openFilterSections.has('Price Range')) {
      priceItem.classList.add('active');
    }

    priceItem.innerHTML = `
      <p class="option-picker-list-item-title">
        <span>Price Range</span>
        <span class="accordion-icon">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L9 1" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </p>
      <div class="option-picker-list-sub price-range-container">
        <div class="price-display-wrapper">
          <span class="display-min">$${this.currentMinPrice !== null ? Math.floor(this.currentMinPrice) : Math.floor(this.allMinPrice)}</span>
          <span class="display-separator">—</span>
          <span class="display-max">$${this.currentMaxPrice !== null ? Math.ceil(this.currentMaxPrice) : Math.ceil(this.allMaxPrice)}</span>
        </div>
        <div class="price-slider-container">
          <div class="price-slider-track"></div>
          <input type="range" class="range-min" min="${this.allMinPrice}" max="${this.allMaxPrice}" value="${this.currentMinPrice !== null ? this.currentMinPrice : this.allMinPrice}" step="1">
          <input type="range" class="range-max" min="${this.allMinPrice}" max="${this.allMaxPrice}" value="${this.currentMaxPrice !== null ? this.currentMaxPrice : this.allMaxPrice}" step="1">
        </div>
        <div class="price-input-wrapper">
          <div class="price-field">
            <label>Min Price</label>
            <div class="input-with-prefix">
              <span>$</span>
              <input type="number" class="price-input input-min" value="${this.currentMinPrice !== null ? this.currentMinPrice : this.allMinPrice}" min="${this.allMinPrice}" max="${this.allMaxPrice}">
            </div>
          </div>
          <div class="price-field">
            <label>Max Price</label>
            <div class="input-with-prefix">
              <span>$</span>
              <input type="number" class="price-input input-max" value="${this.currentMaxPrice !== null ? this.currentMaxPrice : this.allMaxPrice}" min="${this.allMinPrice}" max="${this.allMaxPrice}">
            </div>
          </div>
        </div>
      </div>
    `;

    this.optionPickerList.appendChild(priceItem);
    this.updatePriceSliderUI(priceItem);

    const filterTypesPrefixes = JSON.parse(this.dataset.filterTypesPrefixes || '{}');
    const filterTypesEntries = Object.entries(this.currentFilterTypes);

    filterTypesEntries.forEach(([title, options]) => {
      if (title === 'Price Range') return;

      const typeItem = document.createElement('li');
      typeItem.classList.add('option-picker-list-item');

      if (this.openFilterSections.has(title)) {
        typeItem.classList.add('active');
      }

      const prefix = filterTypesPrefixes[title] || '';

      typeItem.innerHTML = `
        <p class="option-picker-list-item-title">
          <span>${title}</span>
          <span class="accordion-icon">
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </p>
        <ul class="option-picker-list-sub">
          ${options.map(option => {
            const displayOption = prefix && option.startsWith(prefix)
              ? option.replace(prefix, '')
              : option;

            const isChecked = this.selectedOptions.includes(option);

            return `
              <li class="sub-item">
                <div class="sub-item-checkbox">
                  <div class="custom-checkbox${isChecked ? ' checked' : ''}"></div>
                  <p data-option-tag='${option}'>${displayOption}</p>
                </div>
                <div class="sub-item-count">
                  <span>(${this.filterOptionCounts[option] || 0})</span>
                </div>
              </li>
            `;
          }).join('')}
        </ul>
      `;

      this.optionPickerList.appendChild(typeItem);
    });

    this.optionPickerBtn?.classList.remove('hide');
  }

  updatePriceSliderUI(container) {
    const minRange = container.querySelector('.range-min');
    const maxRange = container.querySelector('.range-max');
    const track = container.querySelector('.price-slider-track');
    
    if (!minRange || !maxRange || !track) return;

    const min = parseFloat(minRange.min);
    const max = parseFloat(minRange.max);
    const v1 = parseFloat(minRange.value);
    const v2 = parseFloat(maxRange.value);

    const left = ((v1 - min) / (max - min)) * 100;
    const right = 100 - (((v2 - min) / (max - min)) * 100);

    track.style.left = left + '%';
    track.style.right = right + '%';
  }

  filterAndRenderProducts() {
    if (this.dalstrongLoader) {
      this.dalstrongLoader.classList.add('active');
      const logoWrapper = this.dalstrongLoader.querySelector('.animating-logo-wrapper');
      if (logoWrapper) {
        logoWrapper.insertAdjacentHTML('beforeend', `
          <img 
            src="${logoWrapper.dataset.imageSrc}"
            alt="Animated logo loader"
            width="100"
            height="88"
          />
        `);
      }
    }

    const allProducts = this.allProducts || JSON.parse(sessionStorage.getItem('allProducts')) || [];
    let filteredProducts = allProducts;
    let filteredCustomCardsData = this.customCardsData;

    const groupedOptions = {};
    if (this.selectedOptions.length > 0) {
      this.selectedOptions.forEach(option => {
        Object.entries(this.currentFilterTypes).forEach(([type, options]) => {
          if (options.includes(option)) {
            if (!groupedOptions[type]) {
              groupedOptions[type] = [];
            }
            groupedOptions[type].push(option);
          }
        });
      });
    }

    const filterFn = (node) => {
      const optionMatch = Object.entries(groupedOptions).every(([type, options]) => {
        if (type === 'DEALS & STATUS') {
          return options.every(option => {
            if (option === 'On Sale') return this.calculateDiscount(node) > 0;
            if (option === 'New Arrivals') return node.tags.some(tag => tag.toLowerCase() === 'new');
            if (option === 'In Stock') return node.availableForSale;
            if (option === 'Best Sellers') return node.tags.some(tag => tag.toLowerCase() === 'best_seller' || tag.toLowerCase() === 'best sellers');
            return false;
          });
        }
        if (type === 'Discount') {
          const discount = this.calculateDiscount(node);
          return options.some(option => parseInt(option) === discount);
        }
        return options.some(option => node.tags.includes(option));
      });

      if (!optionMatch) return false;

      const price = this.getFinalPrice(node);
      const min = this.currentMinPrice !== null ? this.currentMinPrice : -Infinity;
      const max = this.currentMaxPrice !== null ? this.currentMaxPrice : Infinity;
      
      return price >= min && price <= max;
    };

    let list = allProducts.filter(({ node }) => filterFn(node));

    if (this.currentSortKey === 'PRICE') {
      list.sort((a, b) => {
        const priceA = this.getFinalPrice(a.node);
        const priceB = this.getFinalPrice(b.node);
        return this.currentReverse ? priceB - priceA : priceA - priceB;
      });
    }

    const inStock = [];
    const outOfStock = [];
    list.forEach(item => {
      (item.node.availableForSale ? inStock : outOfStock).push(item);
    });

    filteredProducts = inStock.concat(outOfStock);

    filteredCustomCardsData = this.customCardsData.filter(card => {
      const product = card.fields.find(field => field.key === 'product').reference;
      return filterFn(product);
    });

    this.generateCustomCards(filteredCustomCardsData);
    this.filteredProducts = filteredProducts;
    this.recalcCountsFromEdges(filteredProducts);
    this.renderFilterTypes();
    this.currentPage = 1;
    const paginatedProducts = this.filteredProducts.slice(0, this.pageSize);
    this.renderProducts(paginatedProducts, false);
    this.renderPaginationControls(this.filteredProducts.length);
    this.renderFilterChips();
  }

  renderFilterChips() {
    let chipsContainer = this.querySelector('.filter-chips');
    if (this.selectedOptions.length === 0) {
      if (chipsContainer) chipsContainer.remove();
      return;
    }

    if (!chipsContainer) {
      chipsContainer = document.createElement('div');
      chipsContainer.className = 'filter-chips';
      const optionPicker = this.querySelector('.option-picker');
      if (optionPicker) {
        optionPicker.after(chipsContainer);
      }
    }

    const filterTypesPrefixes = JSON.parse(this.dataset.filterTypesPrefixes || '{}');

    const chipsHTML = this.selectedOptions.map(option => {
      let displayName = option;
      Object.entries(this.currentFilterTypes).forEach(([type, options]) => {
        if (options.includes(option)) {
          const prefix = filterTypesPrefixes[type] || '';
          if (prefix && option.startsWith(prefix)) {
            displayName = option.replace(prefix, '');
          }
        }
      });
      return `<button class="filter-chip" data-option="${option}">
        <span>${displayName}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>`;
    }).join('');

    const clearLabel = this.hasUrlFilters ? 'View All' : 'Clear All';
    chipsContainer.innerHTML = chipsHTML + `<button class="filter-chip filter-chip--clear">${clearLabel}</button>`;

    chipsContainer.querySelectorAll('.filter-chip:not(.filter-chip--clear)').forEach(chip => {
      chip.addEventListener('click', () => {
        const option = chip.dataset.option;
        this.selectedOptions = this.selectedOptions.filter(o => o !== option);
        // Uncheck the corresponding checkbox in the sidebar
        this.optionPickerList?.querySelectorAll('.sub-item').forEach(item => {
          if (item.querySelector('p')?.dataset.optionTag === option) {
            item.querySelector('.custom-checkbox')?.classList.remove('checked');
          }
        });
        if (this.productsFetched) {
          this.filterAndRenderProducts();
        }
      });
    });

    chipsContainer.querySelector('.filter-chip--clear')?.addEventListener('click', () => {
      this.selectedOptions = [];
      this.hasUrlFilters = false;
      history.replaceState(null, '', window.location.pathname);
      this.optionPickerList?.querySelectorAll('.custom-checkbox').forEach(cb => cb.classList.remove('checked'));
      if (this.productsFetched) {
        this.filterAndRenderProducts();
      }
    });
  }

  updateSelectedOptions(item) {
    const option = item.querySelector('p').dataset.optionTag;
    if (this.selectedOptions.includes(option)) {
      this.selectedOptions = this.selectedOptions.filter(o => o !== option);
    } else {
      this.selectedOptions.push(option);
    }

    if (this.productsFetched) {
      this.filterAndRenderProducts();
    } else {
      this.fetchCollectionProducts(true);
    }
  }

  updateSort(item) {
    const sortKey = item.dataset.sortKey;
    const reverse = item.dataset.reverse === 'true';

    if (this.currentSortKey === sortKey && this.currentReverse === reverse) return;

    this.currentSortKey = sortKey;
    this.currentReverse = reverse;

    this.querySelectorAll('.sort-picker-item').forEach(el => el.classList.remove('active'));
    this.querySelectorAll('.sort-picker-item .custom-radio').forEach(el => el.classList.remove('active'));
    item.classList.add('active');
    const radio = item.querySelector('.custom-radio');
    if (radio) radio.classList.add('active');

    this.closeDrawer();

    if (sortKey === 'PRICE') {
      if (this.productsFetched) {
        this.filterAndRenderProducts();
      } else {
        this.currentPage = 1;
        this.fetchCollectionProducts(true);
      }
    } else {
      this.productsFetched = false;
      this.allProducts = null;
      sessionStorage.removeItem('allProducts');
      this.currentPage = 1;
      this.fetchCollectionProducts(true);
    }
  }

  recalcCountsFromEdges(edges) {
    this.filterOptionCounts = {};

    const inc = (key) => {
      this.filterOptionCounts[key] = (this.filterOptionCounts[key] || 0) + 1;
    };

    edges.forEach(({ node }) => {
      if (!node) return;

      if (this.calculateDiscount(node) > 0) inc('On Sale');
      if (node.tags?.some(t => (t || '').toLowerCase() === 'new')) inc('New Arrivals');
      if (node.availableForSale) inc('In Stock');
      if (node.tags?.some(t => {
        const s = (t || '').toLowerCase();
        return s === 'best_seller' || s === 'best sellers';
      })) inc('Best Sellers');

      if (this.discountTiers?.length > 0) {
        const discount = this.calculateDiscount(node);
        if (discount > 0 && this.discountTiers.includes(discount)) {
          inc(`${discount}%`);
        }
      }

      (node.tags || []).forEach(tag => inc(tag));
    });
  }

  initSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 30,
      grabCursor: 'true',
      mousewheel: window.innerWidth > 768 ? { forceToAxis: true, sensitivity: 1, releaseOnEdges: true } : false,
      breakpoints: {
        320: {
          spaceBetween: 20
        },
        769: {
          spaceBetween: 30
        }
      }
    });
  }

  renderPaginationControls(totalProducts) {
    let paginationContainer = this.querySelector('.collection-pagination');
    if (!paginationContainer) {
      paginationContainer = document.createElement('div');
      paginationContainer.className = 'collection-pagination';
      this.appendChild(paginationContainer);
    }
    paginationContainer.innerHTML = '';

    const displayedCount = Math.min(this.currentPage * this.pageSize, totalProducts);
    const startIndex = totalProducts > 0 ? 1 : 0;
    const endIndex = displayedCount;
    if (totalProducts === 0) {
      paginationContainer.style.display = 'none';
      return;
    }
    paginationContainer.style.display = '';

    const rangeText = document.createElement('div');
    rangeText.className = 'collection-pagination-range';
    rangeText.textContent = `${startIndex} - ${endIndex} of ${totalProducts} PRODUCTS`;
    paginationContainer.appendChild(rangeText);

    if (endIndex < totalProducts) {
      const loadMoreBtn = document.createElement('button');
      loadMoreBtn.className = 'collection-pagination-load-more';
      loadMoreBtn.textContent = 'LOAD MORE';
      paginationContainer.appendChild(loadMoreBtn);
    }
  }
}

customElements.define('collection-grid-new', CollectionGridNew);
